import { getCurrentLanguage } from "../i18n";
import { DATE_TIME_FORMATS, type DateTimeFormatName } from "./catalog";
import { resolveIntlLocale, type FormatDateTimeOptions } from "./formatDateTime";
import { ISO_DATE_RE, toIntlDate } from "./isoDate";
import type { IsoTimestamp, IsoDate } from "./isoTypes";

export const formatDateRangeIntl = (
  start: IsoTimestamp | IsoDate,
  end: IsoTimestamp | IsoDate,
  formatName: DateTimeFormatName,
  options: FormatDateTimeOptions = {},
): string => {
  const intlOptions = DATE_TIME_FORMATS[formatName];
  if (!intlOptions) {
    throw new Error(`Unknown date format name: "${formatName}"`);
  }
  const language = options.locale ?? getCurrentLanguage();
  const intlLocale = resolveIntlLocale(language);
  const dateOnly = ISO_DATE_RE.test(start);
  // Mixing a date-only bound with a timestamp bound would apply the timeZone
  // option inconsistently across the pair, so reject it up front.
  if (dateOnly !== ISO_DATE_RE.test(end)) {
    throw new Error(
      "formatDateRangeIntl: start and end must both be date-only or both be timestamps",
    );
  }
  // Timestamps require an explicit timeZone (consistent with formatDateTime). Without it
  // Intl would silently render in the runtime timezone, producing wrong ranges.
  if (!dateOnly && !options.timeZone) {
    throw new Error("formatDateRangeIntl requires a timeZone for timestamp ranges");
  }
  const opts = !dateOnly ? { ...intlOptions, timeZone: options.timeZone } : intlOptions;
  const formatter = new Intl.DateTimeFormat(intlLocale, opts);
  const startDate = toIntlDate(start);
  const endDate = toIntlDate(end);
  // Intl renders start===end as a degenerate range with a separator; for equal
  // instants, fall back to a single formatted value for clean output.
  if (startDate.getTime() === endDate.getTime()) {
    return formatter.format(startDate);
  }
  return formatter.formatRange(startDate, endDate);
};
